import { useRef, useState, useCallback, useEffect } from "react";
import type { FlowNode, FlowConnection, NodeType } from "@/lib/flow-types";
import { NODE_COLORS } from "@/lib/flow-types";
import { FlowNodeCard } from "./flow-node-card";

interface FlowCanvasProps {
	nodes: FlowNode[];
	connections: FlowConnection[];
	selectedNodeId: string | null;
	onSelectNode: (id: string | null) => void;
	onMoveNode: (id: string, x: number, y: number) => void;
	onDropNewNode: (type: NodeType, x: number, y: number) => void;
	onRemoveNode: (id: string) => void;
	onConnect: (from: string, to: string) => void;
}

function getNodeCenter(node: FlowNode): { x: number; y: number } {
	return { x: node.x + 100, y: node.y + 36 };
}

function getConnectionPath(
	from: FlowNode,
	to: FlowNode
): string {
	const start = getNodeCenter(from);
	const end = getNodeCenter(to);

	const dx = end.x - start.x;
	const dy = end.y - start.y;

	const startY = start.y + 36;
	const endY = end.y - 36;

	if (dy > 40) {
		const midY = startY + (endY - startY) / 2;
		return `M ${start.x} ${startY} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${endY}`;
	}

	const cpOffset = Math.max(60, Math.abs(dx) * 0.3);
	return `M ${start.x} ${startY} C ${start.x} ${startY + cpOffset}, ${end.x} ${endY - cpOffset}, ${end.x} ${endY}`;
}

export function FlowCanvas({
	nodes,
	connections,
	selectedNodeId,
	onSelectNode,
	onMoveNode,
	onDropNewNode,
	onRemoveNode,
	onConnect,
}: FlowCanvasProps) {
	const canvasRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<{
		nodeId: string;
		offsetX: number;
		offsetY: number;
	} | null>(null);
	const [connecting, setConnecting] = useState<{
		fromId: string;
		mouseX: number;
		mouseY: number;
	} | null>(null);
	const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
	const [panning, setPanning] = useState(false);
	const [panStart, setPanStart] = useState({ x: 0, y: 0 });

	const getCanvasCoords = useCallback(
		(clientX: number, clientY: number) => {
			const rect = canvasRef.current?.getBoundingClientRect();
			if (!rect) return { x: clientX, y: clientY };
			return {
				x: clientX - rect.left - canvasOffset.x,
				y: clientY - rect.top - canvasOffset.y,
			};
		},
		[canvasOffset]
	);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === canvasRef.current || (e.target as HTMLElement).closest("[data-canvas-bg]")) {
				onSelectNode(null);
				if (e.button === 0) {
					setPanning(true);
					setPanStart({ x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y });
				}
			}
		},
		[onSelectNode, canvasOffset]
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (panning) {
				setCanvasOffset({
					x: e.clientX - panStart.x,
					y: e.clientY - panStart.y,
				});
				return;
			}

			if (dragging) {
				const coords = getCanvasCoords(e.clientX, e.clientY);
				onMoveNode(
					dragging.nodeId,
					coords.x - dragging.offsetX,
					coords.y - dragging.offsetY
				);
			}
			if (connecting) {
				const coords = getCanvasCoords(e.clientX, e.clientY);
				setConnecting((prev) =>
					prev ? { ...prev, mouseX: coords.x, mouseY: coords.y } : null
				);
			}
		},
		[dragging, connecting, panning, getCanvasCoords, onMoveNode, panStart]
	);

	const handleMouseUp = useCallback(
		(e: React.MouseEvent) => {
			if (panning) {
				setPanning(false);
				return;
			}

			if (connecting) {
				const target = (e.target as HTMLElement).closest(
					"[data-node-id]"
				);
				if (target) {
					const toId = target.getAttribute("data-node-id");
					if (toId && toId !== connecting.fromId) {
						onConnect(connecting.fromId, toId);
					}
				}
				setConnecting(null);
			}
			setDragging(null);
		},
		[connecting, panning, onConnect]
	);

	const handleNodeDragStart = useCallback(
		(nodeId: string, e: React.MouseEvent) => {
			e.stopPropagation();
			const coords = getCanvasCoords(e.clientX, e.clientY);
			const node = nodes.find((n) => n.id === nodeId);
			if (!node) return;
			setDragging({
				nodeId,
				offsetX: coords.x - node.x,
				offsetY: coords.y - node.y,
			});
		},
		[getCanvasCoords, nodes]
	);

	const handleConnectStart = useCallback(
		(nodeId: string, e: React.MouseEvent) => {
			e.stopPropagation();
			const coords = getCanvasCoords(e.clientX, e.clientY);
			setConnecting({ fromId: nodeId, mouseX: coords.x, mouseY: coords.y });
		},
		[getCanvasCoords]
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			const nodeType = e.dataTransfer.getData(
				"application/chatconvert-node"
			) as NodeType;
			if (nodeType) {
				const coords = getCanvasCoords(e.clientX, e.clientY);
				onDropNewNode(nodeType, coords.x - 100, coords.y - 36);
			}
		},
		[getCanvasCoords, onDropNewNode]
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
	}, []);

	const nodeMap = new Map(nodes.map((n) => [n.id, n]));

	return (
		<div
			ref={canvasRef}
			className="relative w-full h-full overflow-hidden bg-[#0d0f14] select-none"
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onDrop={handleDrop}
			onDragOver={handleDragOver}
			style={{ cursor: panning ? "grabbing" : dragging ? "move" : "default" }}
		>
			{/* Grid background */}
			<div
				data-canvas-bg
				className="absolute inset-0 opacity-20"
				style={{
					backgroundImage: `radial-gradient(circle, #4b5563 1px, transparent 1px)`,
					backgroundSize: "24px 24px",
					backgroundPosition: `${canvasOffset.x % 24}px ${canvasOffset.y % 24}px`,
				}}
			/>

			{/* Content layer */}
			<div
				className="absolute"
				style={{
					transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px)`,
				}}
			>
				{/* SVG Connections */}
				<svg
					className="absolute top-0 left-0 pointer-events-none"
					style={{ width: 2000, height: 2000, overflow: "visible" }}
				>
					<defs>
						<marker
							id="arrowhead"
							markerWidth="8"
							markerHeight="6"
							refX="8"
							refY="3"
							orient="auto"
						>
							<polygon
								points="0 0, 8 3, 0 6"
								fill="#94a3b8"
							/>
						</marker>
						{Object.entries(NODE_COLORS).map(([type, color]) => (
							<marker
								key={type}
								id={`arrow-${type}`}
								markerWidth="8"
								markerHeight="6"
								refX="8"
								refY="3"
								orient="auto"
							>
								<polygon
									points="0 0, 8 3, 0 6"
									fill={color}
								/>
							</marker>
						))}
					</defs>

					{connections.map((conn) => {
						const fromNode = nodeMap.get(conn.from);
						const toNode = nodeMap.get(conn.to);
						if (!fromNode || !toNode) return null;

						const path = getConnectionPath(fromNode, toNode);
						const midX =
							(getNodeCenter(fromNode).x +
								getNodeCenter(toNode).x) /
							2;
						const midY =
							(getNodeCenter(fromNode).y +
								getNodeCenter(toNode).y) /
							2;

						return (
							<g key={conn.id}>
								<path
									d={path}
									fill="none"
									stroke={
										NODE_COLORS[fromNode.type] || "#94a3b8"
									}
									strokeWidth={2.5}
									strokeDasharray="none"
									markerEnd={`url(#arrow-${fromNode.type})`}
									className="opacity-60"
								/>
								{conn.label && (
									<>
										<rect
											x={midX - conn.label.length * 3.5 - 6}
											y={midY - 10}
											width={conn.label.length * 7 + 12}
											height={20}
											rx={10}
											fill="#1a1d25"
											stroke={NODE_COLORS[fromNode.type] || "#94a3b8"}
											strokeWidth={1}
											className="opacity-90"
										/>
										<text
											x={midX}
											y={midY + 4}
											textAnchor="middle"
											className="text-[10px] font-medium fill-zinc-400"
										>
											{conn.label}
										</text>
									</>
								)}
							</g>
						);
					})}

					{/* Connecting line while dragging */}
					{connecting && (() => {
						const fromNode = nodeMap.get(connecting.fromId);
						if (!fromNode) return null;
						const start = getNodeCenter(fromNode);
						return (
							<line
								x1={start.x}
								y1={start.y + 36}
								x2={connecting.mouseX}
								y2={connecting.mouseY}
								stroke="#6366f1"
								strokeWidth={2}
								strokeDasharray="6 3"
								className="opacity-70"
							/>
						);
					})()}
				</svg>

				{/* Nodes */}
				{nodes.map((node) => (
					<FlowNodeCard
						key={node.id}
						node={node}
						isSelected={node.id === selectedNodeId}
						onSelect={() => onSelectNode(node.id)}
						onDragStart={(e) => handleNodeDragStart(node.id, e)}
						onConnectStart={(e) =>
							handleConnectStart(node.id, e)
						}
						onRemove={() => onRemoveNode(node.id)}
					/>
				))}
			</div>

			{/* Zoom hint */}
			<div className="absolute bottom-3 right-3 text-[10px] text-zinc-500 bg-[#0d0f14]/80 rounded-full px-3 py-1 backdrop-blur-sm border border-white/[0.06]">
				Click &amp; drag canvas to pan
			</div>
		</div>
	);
}
